/**
 * フィードバック閲覧 — チャットの 👍👎 の一覧（admin 以上・2026-07-02 P3）。
 *
 * 従来はフィードバックを送っても行き先が見えず「送りっぱなし」だった。
 * どの応答に・誰が・どんなコメント付きで評価したかを新しい順に一覧する。
 */
import { useEffect, useState } from "react"
import { Loader2, ThumbsDown, ThumbsUp } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { listFeedback, FeedbackItem } from "@/services/feedbackService"

function formatTimestamp(ms: number): string {
  return new Date(ms).toLocaleString("ja-JP", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Tokyo",
  })
}

export default function FeedbackViewer() {
  const { token } = useAuth()
  const [items, setItems] = useState<FeedbackItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    if (!token) return
    listFeedback(token)
      .then(setItems)
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [token])

  return (
    <div className="p-6 max-w-3xl mx-auto w-full space-y-4">
      <div>
        <h1 className="text-2xl font-bold mb-1">フィードバック</h1>
        <p className="text-sm text-muted-foreground">
          チャットの応答に付いた 👍👎 の一覧です（新しい順・最大100件）。
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          まだフィードバックはありません。
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.feedbackId} className="border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-2 mb-2 text-sm">
                {item.feedbackType === "positive" ? (
                  <ThumbsUp className="h-4 w-4 text-green-600 shrink-0" />
                ) : (
                  <ThumbsDown className="h-4 w-4 text-red-600 shrink-0" />
                )}
                <span className="text-muted-foreground">
                  {formatTimestamp(item.timestamp)}
                </span>
                <span className="font-mono text-xs text-muted-foreground truncate">
                  user: {item.userId.slice(0, 8)}…
                </span>
              </div>
              {item.comment && (
                <p className="text-sm mb-2 whitespace-pre-wrap">{item.comment}</p>
              )}
              <p className="text-xs text-muted-foreground line-clamp-3 whitespace-pre-wrap border-l-2 pl-2">
                {item.message}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
